import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useSearchParams } from "react-router-dom"
import { useActiveClinic } from "../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../../features/subscriptions/hooks/useClinicPlanSnapshot"
import { useClinicSubscription } from "../../features/subscriptions/hooks/useClinicSubscription"
import SettingsModuleScaffold from "./SettingsModuleScaffold"
import { DEFAULT_USER_ID } from "../../config/appConfig"
import {
  resolveClinicLabel,
  SectionCard,
  FieldBlock,
  MetricCard,
  InsightCard,
  TopStateCard,
  EmptyState,
  EditableListItem,
  EditModalShell,
  inputStyle,
  pillStyle,
  listStyle,
  actionButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle
} from "./settingsShared"

const tabs = [
  { key: "sedes", label: "Sedes" },
  { key: "sillas", label: "Sillas" }
]

export default function ClinicResourcesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeClinicId, setActiveClinicId } = useActiveClinic()

  const rawTab = searchParams.get("tab")
  const activeTab = rawTab === "sillas" ? "sillas" : "sedes"

  const clinicId =
    activeClinicId && activeClinicId !== "ALL" ? activeClinicId : null

  const userId =
    localStorage.getItem("odontoflow_user_id") || DEFAULT_USER_ID

  const [clinics, setClinics] = useState([])
  const [chairs, setChairs] = useState([])

  const [loadingClinics, setLoadingClinics] = useState(false)
  const [loadingChairs, setLoadingChairs] = useState(false)

  const [clinicName, setClinicName] = useState("")
  const [clinicPhone, setClinicPhone] = useState("")
  const [clinicEmail, setClinicEmail] = useState("")
  const [clinicAddress, setClinicAddress] = useState("")
  const [savingClinic, setSavingClinic] = useState(false)

  const [chairName, setChairName] = useState("")
  const [savingChair, setSavingChair] = useState(false)

  const [editingClinic, setEditingClinic] = useState(null)
  const [editClinicName, setEditClinicName] = useState("")
  const [editClinicPhone, setEditClinicPhone] = useState("")
  const [editClinicEmail, setEditClinicEmail] = useState("")
  const [editClinicAddress, setEditClinicAddress] = useState("")
  const [savingClinicEdit, setSavingClinicEdit] = useState(false)

  const [editingChair, setEditingChair] = useState(null)
  const [editChairName, setEditChairName] = useState("")
  const [savingChairEdit, setSavingChairEdit] = useState(false)

  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const {
    data: planSnapshot,
    refresh: refreshPlanSnapshot
  } = useClinicPlanSnapshot(clinicId)

  const { subscription } = useClinicSubscription(clinicId)

  const currentPlanName = planSnapshot?.plan?.name || "Sin plan activo"
  const accountName = planSnapshot?.accountName || "tu cuenta"

  const isReadOnly = Boolean(subscription?.access?.isReadOnly)
  const readOnlyReason =
    subscription?.access?.reason || "La clínica está en modo solo lectura."

  const clinicsCount = clinics.length
  const chairsCount = chairs.length

  const activeClinicLabel = useMemo(
    () => resolveClinicLabel(clinics, clinicId),
    [clinics, clinicId]
  )
  const showActiveClinicMetric = clinicsCount > 1 && !!activeClinicLabel

  const handleTabChange = (tab) => {
    setSearchParams({ tab })
    setError("")
    setSuccess("")
  }

  const loadClinics = useCallback(async () => {
    try {
      setLoadingClinics(true)
      const { data } = await axios.get("/api/multi-clinic/user-clinics", {
        params: { userId }
      })
      setClinics(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar las clínicas"
      setError(Array.isArray(message) ? message[0] : message)
      setClinics([])
    } finally {
      setLoadingClinics(false)
    }
  }, [userId])

  const loadChairs = useCallback(async () => {
    if (!clinicId) {
      setChairs([])
      return
    }

    try {
      setLoadingChairs(true)
      const { data } = await axios.get("/api/chairs", {
        params: { clinicId }
      })
      setChairs(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar las sillas"
      setError(Array.isArray(message) ? message[0] : message)
      setChairs([])
    } finally {
      setLoadingChairs(false)
    }
  }, [clinicId])

  useEffect(() => {
    loadClinics()
    loadChairs()
  }, [loadClinics, loadChairs])

  async function handleCreateClinic() {
    try {
      setSavingClinic(true)
      setError("")
      setSuccess("")

      const { data: createdClinic } = await axios.post("/api/multi-clinic/create-clinic", {
        name: clinicName.trim(),
        phone: clinicPhone.trim() || undefined,
        email: clinicEmail.trim() || undefined,
        address: clinicAddress.trim() || undefined
      })

      const createdClinicId = createdClinic?.id || createdClinic?.clinicId

      if (createdClinicId) {
        localStorage.setItem("odontoflow_clinic_id", createdClinicId)
        setActiveClinicId?.(createdClinicId)
      }

      setClinicName("")
      setClinicPhone("")
      setClinicEmail("")
      setClinicAddress("")

      await loadClinics()
      await refreshPlanSnapshot?.()

      window.dispatchEvent(new Event("odontoflow:clinics-updated"))
      window.dispatchEvent(new Event("odontoflow:subscription-updated"))

      setSuccess("Sede creada correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo crear la sede"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingClinic(false)
    }
  }

  async function handleCreateChair() {
    try {
      setSavingChair(true)
      setError("")
      setSuccess("")

      await axios.post("/api/chairs", {
        clinicId,
        name: chairName.trim()
      })

      setChairName("")
      await loadChairs()

      setSuccess("Silla creada correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo crear la silla"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingChair(false)
    }
  }

  function openClinicEditModal(clinicRow) {
    setEditingClinic(clinicRow)
    setEditClinicName(resolveClinicRowName(clinicRow))
    setEditClinicPhone(resolveClinicRowPhone(clinicRow))
    setEditClinicEmail(resolveClinicRowEmail(clinicRow))
    setEditClinicAddress(resolveClinicRowAddress(clinicRow))
    setError("")
    setSuccess("")
  }

  function closeClinicEditModal() {
    if (savingClinicEdit) return
    setEditingClinic(null)
    setEditClinicName("")
    setEditClinicPhone("")
    setEditClinicEmail("")
    setEditClinicAddress("")
  }

  function openChairEditModal(chair) {
    setEditingChair(chair)
    setEditChairName(chair?.name || "")
    setError("")
    setSuccess("")
  }

  function closeChairEditModal() {
    if (savingChairEdit) return
    setEditingChair(null)
    setEditChairName("")
  }

  async function handleSaveClinicEdit() {
    try {
      const targetClinicId =
        editingClinic?.clinicId || editingClinic?.clinic?.id || editingClinic?.id

      if (!targetClinicId) return

      setSavingClinicEdit(true)
      setError("")
      setSuccess("")

      await axios.patch("/api/multi-clinic/update-clinic", {
        clinicId: targetClinicId,
        name: editClinicName.trim(),
        phone: editClinicPhone.trim() || null,
        email: editClinicEmail.trim() || null,
        address: editClinicAddress.trim() || null
      })

      await loadClinics()
      await refreshPlanSnapshot?.()

      window.dispatchEvent(new Event("odontoflow:clinics-updated"))
      closeClinicEditModal()
      setSuccess("Sede actualizada correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo actualizar la sede"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingClinicEdit(false)
    }
  }

  async function handleSaveChairEdit() {
    try {
      if (!editingChair?.id || !clinicId) return

      setSavingChairEdit(true)
      setError("")
      setSuccess("")

      await axios.patch("/api/chairs/update", {
        chairId: editingChair.id,
        clinicId,
        name: editChairName.trim()
      })

      await loadChairs()
      closeChairEditModal()
      setSuccess("Silla actualizada correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo actualizar la silla"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingChairEdit(false)
    }
  }

  const canCreateClinic =
    !savingClinic &&
    !isReadOnly &&
    !!clinicName.trim()

  const canCreateChair =
    !!clinicId &&
    !savingChair &&
    !isReadOnly &&
    !!chairName.trim()

  const canSaveClinicEdit =
    !!editingClinic &&
    !savingClinicEdit &&
    !!editClinicName.trim()

  const canSaveChairEdit =
    !!editingChair?.id &&
    !savingChairEdit &&
    !!editChairName.trim()

  const clinicsStatus = useMemo(() => {
    if (clinicsCount === 0) {
      return {
        label: "Pendiente",
        tone: "warning",
        detail: "Todavía no hay sedes registradas para la cuenta."
      }
    }

    return {
      label: "Operativo",
      tone: "success",
      detail: "La estructura de sedes ya existe y puede seguir creciendo."
    }
  }, [clinicsCount])

  const chairsStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail: "Debes seleccionar una clínica específica para administrar sus sillas."
      }
    }

    if (chairsCount === 0) {
      return {
        label: "Pendiente",
        tone: "warning",
        detail: "La clínica activa todavía no tiene sillas registradas."
      }
    }

    return {
      label: "Operativo",
      tone: "success",
      detail: "La clínica activa ya tiene recursos físicos cargados."
    }
  }, [clinicId, chairsCount])

  const actions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button
        onClick={() => handleTabChange("sedes")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "sedes" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "sedes" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "sedes"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver sedes
      </button>

      <button
        onClick={() => handleTabChange("sillas")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "sillas" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "sillas" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "sillas"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver sillas
      </button>
    </div>
  )

  return (
    <>
      <SettingsModuleScaffold
        eyebrow="Operación de la clínica"
        title="Sedes y recursos"
        subtitle="Concentra sedes, consultorios y sillas dentro de una sola lectura administrativa, sin repartir la infraestructura en pantallas sueltas."
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        actions={actions}
      >
        {isReadOnly ? (
          <TopStateCard tone="danger" title="Modo solo lectura">
            {readOnlyReason}
          </TopStateCard>
        ) : null}

        {activeTab === "sillas" && !clinicId ? (
          <TopStateCard tone="warning" title="Clínica no seleccionada">
            Para administrar sillas y recursos físicos necesitas seleccionar una clínica específica.
          </TopStateCard>
        ) : null}

        {error ? (
          <TopStateCard tone="danger" title="Error">
            {error}
          </TopStateCard>
        ) : null}

        {success ? (
          <TopStateCard tone="success" title="Listo">
            {success}
          </TopStateCard>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 12
          }}
        >
          <MetricCard
            label="Sedes"
            value={loadingClinics ? "..." : String(clinicsCount)}
            helper="Registradas en la cuenta"
            tone={clinicsStatus.tone}
          />

          <MetricCard
            label="Sillas"
            value={loadingChairs ? "..." : clinicId ? String(chairsCount) : "—"}
            helper={clinicId ? "En clínica activa" : "Selecciona clínica"}
            tone={chairsStatus.tone}
          />

          <MetricCard
            label="Plan"
            value={currentPlanName}
            helper={accountName}
            tone="info"
          />

          {showActiveClinicMetric ? (
            <MetricCard
              label="Clínica activa"
              value={activeClinicLabel}
              helper="Contexto actual"
              tone="neutral"
            />
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 12
          }}
        >
          <InsightCard
            title="Sedes y clínicas"
            status={clinicsStatus.label}
            tone={clinicsStatus.tone}
            detail={clinicsStatus.detail}
            ctaLabel="Ir a sedes"
            onClick={() => handleTabChange("sedes")}
          />

          <InsightCard
            title="Sillas y recursos físicos"
            status={chairsStatus.label}
            tone={chairsStatus.tone}
            detail={chairsStatus.detail}
            ctaLabel="Ir a sillas"
            onClick={() => handleTabChange("sillas")}
          />
        </div>

        {activeTab === "sedes" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SectionCard
              title="Alta de sede"
              subtitle="Crea nuevas clínicas o sedes dentro de la cuenta."
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                <FieldBlock label="Nombre">
                  <input
                    value={clinicName}
                    onChange={(e) => setClinicName(e.target.value)}
                    placeholder="Nombre de la clínica"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Teléfono">
                  <input
                    value={clinicPhone}
                    onChange={(e) => setClinicPhone(e.target.value)}
                    placeholder="Teléfono"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Correo">
                  <input
                    value={clinicEmail}
                    onChange={(e) => setClinicEmail(e.target.value)}
                    placeholder="Correo"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Dirección">
                  <input
                    value={clinicAddress}
                    onChange={(e) => setClinicAddress(e.target.value)}
                    placeholder="Dirección"
                    style={inputStyle}
                  />
                </FieldBlock>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={handleCreateClinic}
                  disabled={!canCreateClinic}
                  style={{
                    ...primaryButtonStyle,
                    opacity: canCreateClinic ? 1 : 0.6,
                    cursor: canCreateClinic ? "pointer" : "not-allowed"
                  }}
                >
                  {savingClinic ? "Guardando..." : "Crear sede"}
                </button>
              </div>
            </SectionCard>

            <SectionCard
              title="Listado de sedes"
              subtitle="Sedes registradas dentro de la cuenta actual."
            >
              {loadingClinics ? (
                <EmptyState text="Cargando sedes..." />
              ) : clinics.length === 0 ? (
                <EmptyState text="No hay sedes registradas todavía." />
              ) : (
                <div style={listStyle}>
                  {clinics.map((clinicRow) => (
                    <EditableListItem
                      key={clinicRow.id || clinicRow.clinicId}
                      title={resolveClinicRowName(clinicRow)}
                      metaLeft={resolveClinicRowAddress(clinicRow)}
                      metaRight={clinicRow?.isDefault ? "Principal" : "Sede"}
                      extra={resolveClinicRowEmail(clinicRow) || ""}
                      onEdit={() => openClinicEditModal(clinicRow)}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        ) : null}

        {activeTab === "sillas" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SectionCard
              title="Alta de silla"
              subtitle="Registra recursos físicos para la clínica activa."
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                <FieldBlock label="Nombre">
                  <input
                    value={chairName}
                    onChange={(e) => setChairName(e.target.value)}
                    placeholder="Nombre de la silla"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Estado">
                  <div style={pillStyle}>
                    {clinicId ? "Clínica activa seleccionada" : "Selecciona clínica"}
                  </div>
                </FieldBlock>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={handleCreateChair}
                  disabled={!canCreateChair}
                  style={{
                    ...primaryButtonStyle,
                    opacity: canCreateChair ? 1 : 0.6,
                    cursor: canCreateChair ? "pointer" : "not-allowed"
                  }}
                >
                  {savingChair ? "Guardando..." : "Crear silla"}
                </button>
              </div>
            </SectionCard>

            <SectionCard
              title="Listado de sillas"
              subtitle="Recursos físicos registrados en la clínica activa."
            >
              {loadingChairs ? (
                <EmptyState text="Cargando sillas..." />
              ) : chairs.length === 0 ? (
                <EmptyState text="No hay sillas registradas en esta clínica todavía." />
              ) : (
                <div style={listStyle}>
                  {chairs.map((chair) => (
                    <EditableListItem
                      key={chair.id}
                      title={chair.name || "Silla"}
                      metaLeft={chair.room ? `Área: ${chair.room}` : "Recurso físico"}
                      metaRight="Silla"
                      onEdit={() => openChairEditModal(chair)}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        ) : null}
      </SettingsModuleScaffold>

      {editingClinic ? (
        <EditModalShell
          title="Editar sede"
          subtitle="Ajusta nombre, teléfono, correo y dirección de la sede."
          onClose={closeClinicEditModal}
          actions={
            <>
              <button
                onClick={closeClinicEditModal}
                style={secondaryButtonStyle}
                disabled={savingClinicEdit}
              >
                Cancelar
              </button>

              <button
                onClick={handleSaveClinicEdit}
                disabled={!canSaveClinicEdit}
                style={{
                  ...primaryButtonStyle,
                  opacity: canSaveClinicEdit ? 1 : 0.6,
                  cursor: canSaveClinicEdit ? "pointer" : "not-allowed"
                }}
              >
                {savingClinicEdit ? "Guardando..." : "Guardar cambios"}
              </button>
            </>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12
            }}
          >
            <FieldBlock label="Nombre">
              <input
                value={editClinicName}
                onChange={(e) => setEditClinicName(e.target.value)}
                placeholder="Nombre de la clínica"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Teléfono">
              <input
                value={editClinicPhone}
                onChange={(e) => setEditClinicPhone(e.target.value)}
                placeholder="Teléfono"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Correo">
              <input
                value={editClinicEmail}
                onChange={(e) => setEditClinicEmail(e.target.value)}
                placeholder="Correo"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Dirección">
              <input
                value={editClinicAddress}
                onChange={(e) => setEditClinicAddress(e.target.value)}
                placeholder="Dirección"
                style={inputStyle}
              />
            </FieldBlock>
          </div>
        </EditModalShell>
      ) : null}

      {editingChair ? (
        <EditModalShell
          title="Editar silla"
          subtitle="Ajusta el nombre de la silla registrada en la clínica activa."
          onClose={closeChairEditModal}
          actions={
            <>
              <button
                onClick={closeChairEditModal}
                style={secondaryButtonStyle}
                disabled={savingChairEdit}
              >
                Cancelar
              </button>

              <button
                onClick={handleSaveChairEdit}
                disabled={!canSaveChairEdit}
                style={{
                  ...primaryButtonStyle,
                  opacity: canSaveChairEdit ? 1 : 0.6,
                  cursor: canSaveChairEdit ? "pointer" : "not-allowed"
                }}
              >
                {savingChairEdit ? "Guardando..." : "Guardar cambios"}
              </button>
            </>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12
            }}
          >
            <FieldBlock label="Nombre">
              <input
                value={editChairName}
                onChange={(e) => setEditChairName(e.target.value)}
                placeholder="Nombre de la silla"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Estado">
              <div style={pillStyle}>Clínica activa</div>
            </FieldBlock>
          </div>
        </EditModalShell>
      ) : null}
    </>
  )
}

function resolveClinicRowName(row) {
  return row?.clinic?.name || row?.clinicName || row?.name || "Clínica"
}

function resolveClinicRowPhone(row) {
  return row?.clinic?.phone || row?.phone || ""
}

function resolveClinicRowEmail(row) {
  return row?.clinic?.email || row?.email || ""
}

function resolveClinicRowAddress(row) {
  return row?.clinic?.address || row?.address || "Sin dirección registrada"
}